
import React, {useState, useEffect} from 'react'
import {Table, Form, Button, Spinner, Badge, Dropdown, ButtonGroup, InputGroup, Modal} from 'react-bootstrap'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_RECIPIENTS} from '../../../gql/queries/contactsApiQueries'
import {REMOVE_FROM_RECIPIENTS} from '../../../gql/mutations/recipients'
import {BULK_UNSUBSCRIBE_CONTACTS} from '../../../gql/mutations/subscription'
import {IResRecipientsAPI} from '../../../types/contactsApi'
import {RecipientFilters as FilterTypes, RecipientStats} from '../recipientsTypes'
import {PaginationSection} from '../../shared/PaginationSection'
import {PaginationUtils} from '../../shared/PaginationUtils'
import {BulkTagModal} from './BulkTagModal'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'
import {toast} from 'react-toastify'
import './RecipientList.css'

interface RecipientListProps {
  filters: FilterTypes
  setFilters: React.Dispatch<React.SetStateAction<FilterTypes>>
  selectedContactIds: string[]
  onSelectionChange: (ids: string[]) => void
  segmentId?: string | null
  onStatsChange?: (stats: RecipientStats) => void
}

const PAGE_SIZE = 20

export const RecipientList: React.FC<RecipientListProps> = ({
  filters,
  setFilters,
  selectedContactIds,
  onSelectionChange,
  segmentId,
  onStatsChange,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.search)
  const [currentPage, setCurrentPage] = useState(1)
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search)
  const [showBulkTagModal, setShowBulkTagModal] = useState(false)
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null)

  const [getContacts, {data, loading}] = useLazyQuery<IResRecipientsAPI>(GET_ALL_RECIPIENTS, {
    fetchPolicy: 'network-only',
  })

  const [removeFromRecipients, {loading: removing}] = useMutation(REMOVE_FROM_RECIPIENTS)
  const [bulkUnsubscribe, {loading: unsubscribing}] = useMutation(BULK_UNSUBSCRIBE_CONTACTS)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch contacts when filters or search change
  useEffect(() => {
    const offset = (currentPage - 1) * PAGE_SIZE
    getContacts({
      variables: {
        name: debouncedSearch || undefined,
        first: PAGE_SIZE,
        offset,
        subscriptionStatus: filters.status !== 'ALL' ? filters.status : undefined,
        segment: segmentId || undefined,
      },
    })
  }, [debouncedSearch, currentPage, filters.status, segmentId, getContacts])

  const contacts = data?.allRecipients?.edges || []
  const totalCount = data?.allRecipients?.totalCount || 0
  const stats = data?.recipientStats

  // Pass stats to parent
  useEffect(() => {
    if (stats && onStatsChange) {
      onStatsChange({
        total: stats.totalRecipients,
        subscribed: stats.subscribedCount,
        unsubscribed: stats.unsubscribedCount,
      })
    }
  }, [stats, onStatsChange])
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, PAGE_SIZE, totalCount)

  const isAllSelected = contacts.length > 0 && contacts.every(edge => selectedContactIds.includes(edge.node.id))

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const currentIds = contacts.map(edge => edge.node.id)
      onSelectionChange(selectedContactIds.filter(id => !currentIds.includes(id)))
    } else {
      const currentIds = contacts.map(edge => edge.node.id)
      const combined = [...selectedContactIds, ...currentIds]
      const unique = combined.filter((id, index) => combined.indexOf(id) === index)
      onSelectionChange(unique)
    }
  }

  const toggleSelect = (contactId: string) => {
    if (selectedContactIds.includes(contactId)) {
      onSelectionChange(selectedContactIds.filter(id => id !== contactId))
    } else {
      onSelectionChange([...selectedContactIds, contactId])
    }
  }

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedContactIds.length} contact(s)?`)) {
      return
    }
    // TODO: Implement delete mutation
    alert('Bulk delete not implemented yet')
  }

  const handleRefresh = () => {
    getContacts({
      variables: {
        name: debouncedSearch || undefined,
        first: PAGE_SIZE,
        offset: (currentPage - 1) * PAGE_SIZE,
        // isRecipient: true,
        segment: segmentId || undefined,
      },
    })
  }

  const handleRemoveFromRecipients = async () => {
    if (!window.confirm(`Remove ${selectedContactIds.length} contact(s) from recipients list?`)) {
      return
    }

    try {
      const result = await removeFromRecipients({
        variables: {contactIds: selectedContactIds},
      })
      toast.success(`${result.data.removeFromRecipients.removedCount} contact(s) removed`)
      onSelectionChange([])
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove from recipients')
    }
  }

  const handleBulkUnsubscribe = async () => {
    if (!window.confirm(`Unsubscribe ${selectedContactIds.length} contact(s) from all campaigns?`)) {
      return
    }

    try {
      const result = await bulkUnsubscribe({
        variables: {
          contactIds: selectedContactIds,
          reason: 'Bulk unsubscribe from admin panel',
        },
      })
      toast.success(`${result.data.bulkUnsubscribeContacts.unsubscribedCount} contact(s) unsubscribed`)
      onSelectionChange([])
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || 'Failed to unsubscribe contacts')
    }
  }

  const confirmUnsubscribe = (contactId: string) => {
    setConfirmTargetId(contactId)
  }

  const executeUnsubscribe = async () => {
    if (!confirmTargetId) return
    try {
      await bulkUnsubscribe({
        variables: {
          contactIds: [confirmTargetId],
          reason: 'Unsubscribed from recipients list',
        },
      })
      toast.success('Contact unsubscribed')
      onSelectionChange(selectedContactIds.filter((id) => id !== confirmTargetId))
      setConfirmTargetId(null)
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || 'Failed to unsubscribe contact')
    }
  }



  return (
    <div className='recipient-list'>
      {/* Header with Search */}
      <div className='list-header mb-4'>
        <h5 className='mb-3'>All Recipients</h5>
        <InputGroup>
          <InputGroup.Text>
            <i className='bi bi-search'></i>
          </InputGroup.Text>
          <Form.Control
            type='text'
            placeholder='Search by name, email, or phone...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {/* Bulk Actions */}
      {selectedContactIds.length > 0 && (
        <div className='bulk-actions-bar mb-3'>
          <div className='d-flex justify-content-between align-items-center'>
            <span className='text-muted'>
              <strong>{selectedContactIds.length}</strong> contact{selectedContactIds.length !== 1 && 's'} selected
            </span>
            <div className='d-flex gap-2'>
              <Button variant='outline-primary' size='sm' onClick={() => setShowBulkTagModal(true)}>
                <i className='bi bi-tags me-1'></i>
                Manage Tags
              </Button>
              <Button variant='outline-danger' size='sm' onClick={handleBulkDelete}>
                <i className='bi bi-trash me-1'></i>
                Delete
              </Button>
              <Button variant='outline-warning' size='sm' onClick={handleRemoveFromRecipients} disabled={removing}>
                <i className='bi bi-person-dash me-1'></i>
                {removing ? 'Removing...' : 'Remove from Recipients'}
              </Button>
              <Button variant='outline-danger' size='sm' onClick={handleBulkUnsubscribe} disabled={unsubscribing}>
                <i className='bi bi-envelope-slash me-1'></i>
                {unsubscribing ? 'Unsubscribing...' : 'Bulk Unsubscribe'}
              </Button>
              <Button variant='outline-secondary' size='sm' onClick={() => onSelectionChange([])}>
                <i className='bi bi-x me-1'></i>
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className='table-container'>
        {loading && contacts.length === 0 ? (
          <div className='text-center py-5'>
            <Spinner animation='border' />
          </div>
        ) : contacts.length === 0 ? (
          <div className='text-center py-5 text-muted'>
            <i className='bi bi-inbox fs-1 d-block mb-2'></i>
            <p>No recipients found</p>
          </div>
        ) : (
          <Table hover responsive>
            <thead>
              <tr>
                <th style={{width: '50px'}}>
                  <Form.Check
                    type='checkbox'
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Tags</th>
                <th>Status</th>
                <th style={{width: '100px'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(({node: contact}) => (
                <tr key={contact.id}>
                  <td>
                    <Form.Check
                      type='checkbox'
                      checked={selectedContactIds.includes(contact.id)}
                      onChange={() => toggleSelect(contact.id)}
                    />
                  </td>
                  <td>
                    <strong>{contact.fullName || `${contact.firstName} ${contact.lastName}`}</strong>
                  </td>
                  <td>{contact.email || <span className='text-muted'>—</span>}</td>
                  <td>
                    <PhoneProviderActions phone={contact.phone} emptyText='—' />
                  </td>
                  <td>
                    {contact.tagAssignments && contact.tagAssignments.length > 0 ? (
                      <div className='d-flex flex-wrap gap-1'>
                        {contact.tagAssignments.map((assignment) => (
                          <span
                            key={assignment.tag.id}
                            className='badge rounded-pill'
                            style={{
                              backgroundColor: assignment.tag.color + '20', // 20% opacity
                              color: assignment.tag.color,
                              border: `1px solid ${assignment.tag.color}`,
                              fontSize: '0.7rem',
                            }}
                          >
                            {assignment.tag.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className='text-muted small'>No tags</span>
                    )}
                  </td>
                  <td>
                    {contact.subscriptionStatus === 'SUBSCRIBED' ? (
                      <Badge bg='success' className='subscription-badge'>
                        <i className='bi bi-check-circle me-1'></i>
                        Subscribed
                      </Badge>
                    ) : (
                      <Badge bg='danger' className='subscription-badge'>
                        <i className='bi bi-x-circle me-1'></i>
                        Unsubscribed
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Dropdown as={ButtonGroup} size='sm'>
                      <Button variant='outline-primary' size='sm'>
                        View
                      </Button>
                      <Dropdown.Toggle split variant='outline-primary' size='sm' />
                      <Dropdown.Menu>
                        <Dropdown.Item>
                          <i className='bi bi-pencil me-2'></i>
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item>
                          <i className='bi bi-tags me-2'></i>
                          Manage Tags
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          className='text-danger'
                          onClick={() => confirmUnsubscribe(contact.id)}
                          disabled={unsubscribing}
                        >
                          <i className='bi bi-x-circle me-2'></i>
                          {unsubscribing ? 'Unsubscribing...' : 'Unsubscribe'}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationSection
          first={PAGE_SIZE}
          setFirst={() => {}}
          currentPage={currentPage}
          totalPages={totalPages}
          start={start}
          end={end}
          totalPolicies={totalCount}
          updatePage={setCurrentPage}
          PaginationUtils={PaginationUtils}
        />
      )}

      {/* Bulk Tag Modal */}
      <BulkTagModal
        show={showBulkTagModal}
        onHide={() => setShowBulkTagModal(false)}
        selectedContactIds={selectedContactIds}
        onSuccess={() => {
          handleRefresh()
          onSelectionChange([])
        }}
      />

      <Modal show={Boolean(confirmTargetId)} onHide={() => setConfirmTargetId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm unsubscribe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This contact will be unsubscribed from all campaigns. Do you want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setConfirmTargetId(null)} disabled={unsubscribing}>
            Cancel
          </Button>
          <Button variant='danger' onClick={executeUnsubscribe} disabled={unsubscribing}>
            {unsubscribing ? 'Unsubscribing...' : 'Unsubscribe'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
